import * as Headless from '@headlessui/react'
import clsx from 'clsx'
import React, { forwardRef } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { displayFont } from 'fuse_common/utils/ui/fonts'
import { Link, LinkProps } from 'fuse_common/components/ui/link'
import { Spinner } from 'fuse_common/components/ui/spinner'

const baseClassName = clsx(
  'relative isolate inline-flex items-center justify-center',
  'cursor-button ring-offset-2 ring-offset-background data-[disabled]:cursor-default transition-all',
)
const nonLinkClassName = 'outline-none focus-visible:ring-2 ring-fuse-orange'
const fontSizeClassName = 'text-button'
const paddingClassName = 'py-1 px-3'
const largeClassName = 'py-4 px-6'
const circleClassName = 'text-grey1 !rounded-full'
const smallCircleClassName = 'p-2 size-8'
const largeCircleClassName = 'p-4 size-14'
const promoClassName = 'py-4 px-6 text-header3 rounded-full'
const roundedClassName = 'rounded-large-button'
const appearanceClassName = {
  primary:
    'text-white bg-button1 data-[disabled]:bg-button-primary-inactive data-[disabled]:contrast-[60%] data-[disabled]:dark:contrast-[100%] data-[disabled]:dark:bg-button-primary-inactive data-[disabled]:dark:text-black data-[hover]:bg-button3 border-transparent shadow-overlap',
  secondary:
    'dark:border-transparent bg-button-secondary dark:bg-button-secondary data-[hover]:bg-button-secondary-active data-[hover]:dark:bg-button-secondary-active text-fuse-orange data-[disabled]:text-secondary data-[disabled]:bg-button-secondary-inactive border-2 border-edge shadow-overlap',
  tertiary:
    'border-grey4 bg-surface data-[hover]:bg-active-surface data-[disabled]:text-secondary data-[disabled]:bg-grey6 shadow-raised',
  plain:
    'text-primary bg-background data-[disabled]:bg-grey4 data-[disabled]:text-secondary data-[hover]:bg-grey5 data-[hover]:dark:bg-grey6 data-[disabled]:dark:bg-grey5 border-transparent',
  none: 'border-transparent',
  link: 'text-fuse-orange !p-0',
}

export type ButtonProps<T extends string> = {
  appearance: keyof typeof appearanceClassName
  circle?: boolean
  rounded?: boolean
  fontSize?: boolean
  promo?: boolean
  className?: string
  children: React.ReactNode
  large?: boolean
  loading?: boolean
} & ({ circle: true; 'aria-label': string } | {}) &
  (Omit<Headless.ButtonProps, 'className'> | LinkProps<T>)

export const Button = forwardRef(function Button<T extends string>(
  {
    appearance,
    circle,
    promo,
    className,
    children,
    rounded = true,
    fontSize = true,
    large = false,
    loading = false,
    ...props
  }: ButtonProps<T>,
  ref: React.ForwardedRef<HTMLElement>,
) {
  const classes = clsx(
    className,
    baseClassName,
    !('href' in props) && nonLinkClassName,
    circle && circleClassName,
    circle && (large ? largeCircleClassName : smallCircleClassName),
    promo && promoClassName,
    promo && displayFont.className,
    !promo && !circle && !large && paddingClassName,
    !promo && fontSize && fontSizeClassName,
    rounded && !circle && !promo && roundedClassName,
    appearanceClassName[appearance],
    large && !circle && largeClassName,
  )

  const spinner = (
    <AnimatePresence>
      {loading && (
        <motion.div key="text" initial={sideSpinnerInitial} animate={sideSpinnerAnimate} exit={sideSpinnerExit}>
          <Spinner key="spinner" className="ml-2" />
        </motion.div>
      )}
    </AnimatePresence>
  )

  return 'href' in props && props.href ? (
    <Link
      {...props}
      data-disabled={(props as any).disabled || loading ? true : undefined}
      className={classes}
      ref={ref as React.ForwardedRef<HTMLAnchorElement>}
    >
      <TouchTarget>
        {children}
        {spinner}
      </TouchTarget>
    </Link>
  ) : (
    <Headless.Button
      {...(props as any)}
      disabled={(props as any).disabled || loading}
      className={clsx(classes, 'cursor-button')}
      ref={ref}
    >
      <TouchTarget>
        {children}
        {spinner}
      </TouchTarget>
    </Headless.Button>
  )
}) as <T extends string>(
  props: ButtonProps<T> & { ref?: React.ForwardedRef<HTMLButtonElement | HTMLAnchorElement> },
) => React.ReactElement<any>

/**
 * Expand the hit area to at least 44×44px on touch devices
 */
export function TouchTarget({ children }: { children: React.ReactNode }) {
  return (
    <>
      <span
        className="absolute left-1/2 top-1/2 size-[max(100%,2.75rem)] -translate-x-1/2 -translate-y-1/2 [@media(pointer:fine)]:hidden"
        aria-hidden="true"
      />
      {children}
    </>
  )
}

const sideSpinnerInitial = { opacity: 0, width: 0 }
const sideSpinnerAnimate = { opacity: 1, width: 'auto' }
const sideSpinnerExit = { opacity: 0, width: 0 }
